import {
    ExamDetails,
    ExamList,
    WriteRecommendations,
    Rate,
    VerificationCodeSendingStatus,
    SendSignatureVerificationCode,
    SignExamProtocol,
    FilteredExamStatus,
    Exam,
    ISignCurrentSovietProtocols,
    SignCurrentSovietProtocols,
    ISendCodeForSignCurrentSovietProtocols,
    SendCodeForSignCurrentSovietProtocols,
} from '../../api/ApiClient';
import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import moment from 'moment';

const initialState: IExamsState = {
    examsList: null,
    needingAttentionExamIds: [],
    examsInProcessingList: [],
    examsForSigningSovietProtocolsList: [],
    examDetails: null,
    examStarted: false,
    smsCodeStatus: null,
};

const exams = createModel({
    state: initialState,
    reducers: {
        setExams(state: IExamsState, examsList: IExamsState['examsList']): IExamsState {
            return { ...state, examsList };
        },
        setExamsInProcessing(state: IExamsState, examsInProcessingList: IExamsState['examsInProcessingList']): IExamsState {
            return { ...state, examsInProcessingList };
        },
        setNeedingAttentionExamIds(state: IExamsState, needingAttentionExamIds: string[]): IExamsState {
            return { ...state, needingAttentionExamIds };
        },
        setExamStarted(state: IExamsState, examStarted: boolean): IExamsState {
            return { ...state, examStarted };
        },
        setExamDetails(state: IExamsState, examDetails: ExamDetails): IExamsState {
            return { ...state, examDetails };
        },
        setSmsCodeStatus(state: IExamsState, smsCodeStatus: VerificationCodeSendingStatus | null): IExamsState {
            return { ...state, smsCodeStatus };
        },
        setExamsForSigningSovietProtocolsList(state: IExamsState, examsForSigningSovietProtocolsList: Exam[] | null): IExamsState {
            return { ...state, examsForSigningSovietProtocolsList };
        },
        clearExamDetails(state) {
            return { ...state, examDetails: null };
        },
    },
    effects: (dispatch: any) => ({
        async fetchExamsInProcessing() {
            const examList = await apiConnector.execApi(dispatch, api => api.getExamsInProcessing());

            this.setExamsInProcessing(examList);
        },
        async fetchExamsForSigningSovietProtocols() {
            const examsForSigningSovietProtocolsList = await apiConnector.execApi(dispatch, api => api.getExamsForSigningSovietProtocols());

            this.setExamsForSigningSovietProtocolsList(examsForSigningSovietProtocolsList);
        },
        async fetchExams(payload: GetExamsQuery) {
            const examList = await apiConnector.execApi(
                dispatch,
                api => api.getExams(
                    payload.page,
                    payload.pageSize,
                    payload.sortOrder,
                    payload.sortField,
                ),
            );
            const needingAttentionExamIds = await apiConnector.execApi(
                dispatch,
                api => api.getNeedingAttentionExamIds(),
            );
            this.setExams(examList);
            this.setNeedingAttentionExamIds(needingAttentionExamIds);
        },
        async fetchExamFilteredList(payload: IGetExamsList) {
            const examList = await apiConnector.execApi(
                dispatch,
                api => api.getExamFilteredList(
                    payload.qualificationIds,
                    payload.examinationCenterIds,
                    payload.filteredExamStatus,
                    payload.from,
                    payload.to,
                    payload.page,
                    payload.pageSize,
                    payload.sortOrder,
                    payload.sortField,
                ),
            );
            const needingAttentionExamIds = await apiConnector.execApi(
                dispatch,
                api => api.getNeedingAttentionExamIds(),
            );
            this.setExams(examList);
            this.setNeedingAttentionExamIds(needingAttentionExamIds);
        },
        async startExam(examId: string) {
            this.setExamDetails(await apiConnector.execApi(
                dispatch,
                api => api.startExam(examId),
            ));
        },
        async fetchExamDetails(examId: string) {
            dispatch.requests.setSmsCodeStatus(null);
            const exam = await apiConnector.execApi(dispatch, api => api.getExam(examId));

            this.setExamDetails(exam);

            return exam;
        },
        async sendNokPasswordAgain(data: ISendSovietPasswordAgain) {
            const smsCodeStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForSignNokProtocol(data.examId, new SendSignatureVerificationCode({ captcha: data.captcha })));

            if (smsCodeStatus) {
                dispatch.notifications.addNotification([{ message: 'Код успешно отправлен!', level: 'success' }]);
            }

            this.setSmsCodeStatus(smsCodeStatus);

            return smsCodeStatus;
        },
        async sendSovietPasswordAgain(data: ISendSovietPasswordAgain) {
            const smsCodeStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForSignSovietProtocol(data.examId, new SendSignatureVerificationCode({ captcha: data.captcha })));

            if (smsCodeStatus) {
                dispatch.notifications.addNotification([{ message: 'Код успешно отправлен!', level: 'success' }]);
            }

            this.setSmsCodeStatus(smsCodeStatus);

            return smsCodeStatus;
        },
        async sendAkPasswordAgain(data: ISendSovietPasswordAgain) {
            const smsCodeStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForSignAkProtocol(data.examId, new SendSignatureVerificationCode({ captcha: data.captcha })));

            if (smsCodeStatus) {
                dispatch.notifications.addNotification([{ message: 'Код успешно отправлен!', level: 'success' }]);
            }

            this.setSmsCodeStatus(smsCodeStatus);

            return smsCodeStatus;
        },
        async submitNokSignCode({ examId, code, captcha }: ISendCode) {
            const examDetails = await apiConnector.execApi(
                dispatch,
                api => api.signNokProtocol(examId, new SignExamProtocol({ code, captcha })),
            );
            if (examDetails) {
                this.setExamDetails(examDetails);
                this.setSmsCodeStatus(null);
                dispatch.notifications.addNotification([{ message: 'Протокол успешно подписан!', level: 'success' }]);
            }
            return Boolean(examDetails);
        },
        async submitSovietSignCode({ examId, code, captcha }: ISendCode) {
            const examDetails = await apiConnector.execApi(
                dispatch,
                api => api.signSovietProtocol(examId, new SignExamProtocol({ code, captcha })),
            );
            if (examDetails) {
                this.setExamDetails(examDetails);
                this.setSmsCodeStatus(null);
                dispatch.notifications.addNotification([{ message: 'Протокол успешно подписан!', level: 'success' }]);
            }
            return Boolean(examDetails);
        },
        async submitAkSignCode({ examId, code, captcha }: ISendCode) {
            const examDetails = await apiConnector.execApi(
                dispatch,
                api => api.signAkProtocol(examId, new SignExamProtocol({ code, captcha })),
            );
            if (examDetails) {
                this.setExamDetails(examDetails);
                this.setSmsCodeStatus(null);
                dispatch.notifications.addNotification([{ message: 'Протокол успешно подписан!', level: 'success' }]);
            }
            return Boolean(examDetails);
        },
        async writeRecommendationsAsync({ examId, body }: IWriteRecommendation) {
            const details = await apiConnector.execApi(dispatch, api => api.writeRecommendations(examId, body));
            if (details) {
                dispatch.notifications.addNotification([{ message: 'Рекомендации успешно добавлены!', level: 'success' }]);
            }
            this.setExamDetails(details);
        },
        async rateAsync({ examId, body }: IRateExam) {
            const details = await apiConnector.execApi(dispatch, api => api.rate(examId, body));
            if (details) {
                dispatch.notifications.addNotification([{ message: 'Рекомендации успешно добавлены!', level: 'success' }]);
            }
            this.setExamDetails(details);
        },
        async signCurrentSovietProtocolsAsync(body: ISignCurrentSovietProtocols) {
            const res = await apiConnector.execApi(dispatch, api => api.signCurrentSovietProtocols(new SignCurrentSovietProtocols(body)));

            return res;
        },
        async sendCodeForSignCurrentSovietProtocolsAsync(body: ISendCodeForSignCurrentSovietProtocols) {
            const smsCodeStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForSignCurrentSovietProtocols(new SendCodeForSignCurrentSovietProtocols(body)));

            if (smsCodeStatus) {
                dispatch.notifications.addNotification([{ message: 'Код успешно отправлен!', level: 'success' }]);
            }

            this.setSmsCodeStatus(smsCodeStatus);

            return smsCodeStatus;
        },
    }),
});

export interface IGetExamsList {
    qualificationIds?: string[];
    examinationCenterIds?: string[];
    qecIds?: string[];
    filteredExamStatus?: FilteredExamStatus | undefined;
    from?: moment.Moment;
    to?: moment.Moment;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

interface IWriteRecommendation {
    examId: string;
    body: WriteRecommendations;
}

interface IRateExam {
    examId: string;
    body: Rate;
}

interface ISendSovietPasswordAgain {
    examId: string;
    captcha?: string;
}

interface IExamsState {
    examsList: ExamList | null;
    examsInProcessingList: Exam[] | null;
    examsForSigningSovietProtocolsList: Exam[] | null;
    needingAttentionExamIds: string[];
    examDetails: ExamDetails | null;
    examStarted: boolean;
    smsCodeStatus: VerificationCodeSendingStatus | null;
}

interface GetExamsQuery {
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
}

interface ISendCode {
    examId: string;
    code: string;
    captcha?: string;
}

export default exams;
